import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Table } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import CashSessionActionModal from '../components/CashSessionActionModal';
import CashHistoryPanel from '../components/CashHistoryPanel';
import CashSessionSummaryTable from '../components/CashSessionSummaryTable';
import cashSessionService from '../services/cashSessionService';

function requestLabel(type) {
  if (type === 'OPEN') return 'Ouverture';
  if (type === 'APPORT') return 'Apport';
  if (type === 'RETRAIT') return 'Retrait';
  if (type === 'CLOSE') return 'Clôture';
  return type;
}

function requestVariant(type) {
  if (type === 'OPEN') return 'primary';
  if (type === 'APPORT') return 'success';
  if (type === 'RETRAIT') return 'warning';
  if (type === 'CLOSE') return 'danger';
  return 'secondary';
}

function statusVariant(status) {
  if (status === 'PENDING') return 'warning';
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'secondary';
  return 'light';
}

function buildRequestDetail(row) {
  if (!row) return '—';

  if (row.requestType === 'OPEN') {
    return `Open USD ${Number(row.payload?.openUsd || 0).toFixed(2)} / HTG ${Number(row.payload?.openHtg || 0).toFixed(2)}`;
  }

  if (row.requestType === 'APPORT' || row.requestType === 'RETRAIT') {
    return `${row.currency || ''} ${Number(row.amount || 0).toFixed(2)}`;
  }

  if (row.requestType === 'CLOSE') {
    return [
      `Solde système USD ${Number(row.payload?.systemBalanceUsd || 0).toFixed(2)}`,
      `HTG ${Number(row.payload?.systemBalanceHtg || 0).toFixed(2)}`,
      `| Déclaré utilisateur USD ${Number(row.payload?.requesterRealBalanceUsd || 0).toFixed(2)}`,
      `HTG ${Number(row.payload?.requesterRealBalanceHtg || 0).toFixed(2)}`,
    ].join(' ');
  }

  return '—';
}

export default function CashSessionPage() {
  const { hasRight } = useContext(AuthContext);
  const canView = hasRight('CASH_VIEW');
  const canOpen = hasRight('CASH_OPEN');
  const canApport = hasRight('CASH_APPORT');
  const canWithdraw = hasRight('CASH_WITHDRAW');
  const canClose = hasRight('CASH_CLOSE');
  const canHistory = hasRight('CASH_HISTORY_VIEW');
  const canEditOpeningAmounts = hasRight('CASH_OPEN_AMOUNT_EDIT');

  const [overview, setOverview] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionModal, setActionModal] = useState({ show: false, mode: 'requestOpen' });

  const currentSession = overview?.currentSession || null;
  const previousBalance = overview?.previousBalance || { usd: 0, htg: 0 };
  const pendingRequests = overview?.pendingRequests || [];
  const pendingOpen = pendingRequests.find((row) => row.requestType === 'OPEN');

  const currentSessionRequests = useMemo(() => {
    return Array.isArray(currentSession?.requests) ? currentSession.requests : [];
  }, [currentSession]);

  const loadOverview = async () => {
    if (!canView) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const payload = await cashSessionService.me();
      setOverview(payload);
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || 'Erreur chargement de la caisse.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [canView]);

  const submitAction = async (values) => {
    try {
      if (actionModal.mode === 'requestOpen') {
        await cashSessionService.requestOpen({
          openUsd: values.openUsd || 0,
          openHtg: values.openHtg || 0,
          note: values.note || null,
        });
      } else if (actionModal.mode === 'requestApport') {
        await cashSessionService.requestApport({
          currency: values.currency,
          amount: values.amount,
          note: values.note || null,
        });
      } else if (actionModal.mode === 'requestRetrait') {
        await cashSessionService.requestRetrait({
          currency: values.currency,
          amount: values.amount,
          note: values.note || null,
        });
      } else if (actionModal.mode === 'requestClose') {
        await cashSessionService.requestClose({
          realBalanceUsd: values.realBalanceUsd || 0,
          realBalanceHtg: values.realBalanceHtg || 0,
          note: values.note || null,
        });
      }

      setActionModal({ show: false, mode: 'requestOpen' });
      await loadOverview();
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || 'Erreur de demande de caisse.');
    }
  };

  const currentStatusBadge = useMemo(() => {
    if (!currentSession) return null;
    return (
      <Badge bg={currentSession.status === 'OPEN' ? 'success' : 'warning'}>
        {currentSession.status}
      </Badge>
    );
  }, [currentSession]);

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Gestion de la caisse</h2>
        <Button variant="outline-secondary" onClick={loadOverview} disabled={loading}>
          Actualiser
        </Button>
      </div>

      {!canView && <Alert variant="warning">Vous n’avez pas accès à la caisse.</Alert>}
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      {canView && (
        <>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Ma caisse</h5>
                  <div className="text-muted small">
                    Une seule caisse couvre tous les modules auxquels vous avez accès.
                  </div>
                </div>
                {currentStatusBadge}
              </div>

              {!currentSession && !pendingOpen && (
                <Alert variant="info" className="mb-3">
                  Aucune caisse active. Utilise le bouton ci-dessous pour envoyer une demande d’ouverture.
                </Alert>
              )}

              {!currentSession && pendingOpen && (
                <Alert variant="warning" className="mb-3">
                  Une demande d’ouverture est déjà en attente depuis{' '}
                  {new Date(pendingOpen.requestedAt).toLocaleString()}.
                </Alert>
              )}

              {currentSession ? (
                <>
                  <CashSessionSummaryTable session={currentSession} showRequesterClose />
                  {currentSession.status === 'PENDING_CLOSE' && (
                    <Alert variant="warning" className="mt-3 mb-0">
                      Une demande de clôture est en attente de validation. Aucune opération n’est autorisée tant que l’administrateur n’a pas confirmé ou rejeté cette demande.
                    </Alert>
                  )}
                </>
              ) : null}

              <div className="d-flex gap-2 flex-wrap mt-3">
                {!currentSession && !pendingOpen && canOpen && (
                  <Button onClick={() => setActionModal({ show: true, mode: 'requestOpen' })}>
                    Ouvrir ma caisse
                  </Button>
                )}
                {currentSession?.status === 'OPEN' && canApport && (
                  <Button
                    variant="outline-success"
                    onClick={() => setActionModal({ show: true, mode: 'requestApport' })}
                  >
                    Demander un apport
                  </Button>
                )}
                {currentSession?.status === 'OPEN' && canWithdraw && (
                  <Button
                    variant="outline-warning"
                    onClick={() => setActionModal({ show: true, mode: 'requestRetrait' })}
                  >
                    Demander un retrait
                  </Button>
                )}
                {currentSession?.status === 'OPEN' && canClose && (
                  <Button
                    variant="outline-danger"
                    onClick={() => setActionModal({ show: true, mode: 'requestClose' })}
                  >
                    Demander la clôture
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h5 className="mb-3">Demandes liées à la session en cours</h5>

              {!currentSession ? (
                <Alert variant="light" className="mb-0">
                  Aucune session en cours. Cette liste sera réinitialisée tant qu’une nouvelle session n’est pas ouverte.
                </Alert>
              ) : (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Statut</th>
                      <th>Détail</th>
                      <th>Demandé par</th>
                      <th>Traité par</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSessionRequests.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted">
                          Aucune demande liée à cette session.
                        </td>
                      </tr>
                    ) : (
                      currentSessionRequests.map((row) => (
                        <tr key={row.id}>
                          <td>{row.requestedAt ? new Date(row.requestedAt).toLocaleString() : '—'}</td>
                          <td>
                            <Badge bg={requestVariant(row.requestType)}>
                              {requestLabel(row.requestType)}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={statusVariant(row.status)}>
                              {row.status}
                            </Badge>
                          </td>
                          <td>{buildRequestDetail(row)}</td>
                          <td>{row.requestedByUser?.username || currentSession.user?.username || '—'}</td>
                          <td>{row.approvedByUser?.username || row.rejectedByUser?.username || '—'}</td>
                          <td>{row.decisionNote || row.requesterNote || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {canHistory && <CashHistoryPanel />}
        </>
      )}

      <CashSessionActionModal
        show={actionModal.show}
        mode={actionModal.mode}
        session={currentSession}
        previousBalance={previousBalance}
        canEditOpeningAmounts={canEditOpeningAmounts}
        onHide={() => setActionModal({ show: false, mode: 'requestOpen' })}
        onSubmit={submitAction}
      />

    </Container>
  );
}
